// Skin painters: 64x64 Minecraft-layout skins drawn in code (plus an emissive layer).
import { hexToRgb, shade, RNG } from './util.js';

export const SKIN_TONES = ['#f3d2b3', '#e5b48a', '#c98e5f', '#a86d43', '#865330', '#5c3a22'];
export const HAIR_COLORS = ['#1c1410', '#3b2416', '#6b4423', '#d4a84f', '#a8432a', '#4a5260'];
export const HAIR_NAMES = ['Black', 'Dark Brown', 'Brown', 'Blonde', 'Ginger', 'Grey'];

const LAYOUT = {
  head: [0, 0, 8, 8, 8],
  hat: [32, 0, 8, 8, 8],
  body: [16, 16, 8, 12, 4],
  jacket: [16, 32, 8, 12, 4],
  armR: [40, 16, 4, 12, 4],
  armL: [32, 48, 4, 12, 4],
  legR: [0, 16, 4, 12, 4],
  legL: [16, 48, 4, 12, 4],
};

export function faceRects(u, v, w, h, d) {
  return {
    top: [u + d, v, w, d],
    bottom: [u + d + w, v, w, d],
    right: [u, v + d, d, h],
    front: [u + d, v + d, w, h],
    left: [u + d + w, v + d, d, h],
    back: [u + d + w + d, v + d, w, h],
  };
}
export const PART_LAYOUT = LAYOUT;

class SkinPainter {
  constructor(seed = 7) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvas.height = 64;
    this.glowCanvas = document.createElement('canvas');
    this.glowCanvas.width = this.glowCanvas.height = 64;
    this.img = new ImageData(64, 64);
    this.gimg = new ImageData(64, 64);
    for (let i = 3; i < this.gimg.data.length; i += 4) this.gimg.data[i] = 255;
    this.r = new RNG(seed);
    this.hasGlow = false;
  }
  px(x, y, col, a = 255) {
    if (x < 0 || y < 0 || x >= 64 || y >= 64) return;
    const i = (y * 64 + x) * 4;
    if (col === null) {
      this.img.data[i + 3] = 0;
      return;
    }
    const c = typeof col === 'string' ? hexToRgb(col) : col;
    this.img.data[i] = c[0];
    this.img.data[i + 1] = c[1];
    this.img.data[i + 2] = c[2];
    this.img.data[i + 3] = a;
  }
  glow(x, y, col) {
    const i = (y * 64 + x) * 4;
    const c = typeof col === 'string' ? hexToRgb(col) : col;
    this.gimg.data[i] = c[0];
    this.gimg.data[i + 1] = c[1];
    this.gimg.data[i + 2] = c[2];
    this.hasGlow = true;
  }
  rect(x, y, w, h, col, noise = 0) {
    for (let j = 0; j < h; j++)
      for (let i = 0; i < w; i++) {
        if (noise > 0) {
          const f = (this.r.next() - 0.5) * noise;
          this.px(x + i, y + j, shade(col, f));
        } else this.px(x + i, y + j, col);
      }
  }
  faces(part) {
    const L = LAYOUT[part];
    return faceRects(L[0], L[1], L[2], L[3], L[4]);
  }
  // fill all 6 faces of a part
  fill(part, col, noise = 0.06) {
    const F = this.faces(part);
    for (const k of Object.keys(F)) this.rect(...F[k], col, noise);
  }
  // fill rows [y0,y1) of the 4 side faces of a part, plus top if y0===0 / bottom if y1===h
  band(part, y0, y1, col, noise = 0.06) {
    const F = this.faces(part);
    for (const k of ['front', 'back', 'left', 'right']) {
      const [x, y, w] = F[k];
      this.rect(x, y + y0, w, y1 - y0, col, noise);
    }
    const h = LAYOUT[part][3];
    if (y0 === 0) this.rect(...F.top, col, noise);
    if (y1 === h) this.rect(...F.bottom, col, noise);
  }
  // pixel in a face's local coords
  f(part, face, x, y, col) {
    const [fx, fy] = this.faces(part)[face];
    this.px(fx + x, fy + y, col);
  }
  fg(part, face, x, y, col, glowCol) {
    this.f(part, face, x, y, col);
    const [fx, fy] = this.faces(part)[face];
    this.glow(fx + x, fy + y, glowCol || col);
  }
  clearHat() {
    for (const part of ['hat', 'jacket']) {
      const F = this.faces(part);
      for (const k of Object.keys(F)) {
        const [x, y, w, h] = F[k];
        for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) this.px(x + i, y + j, null);
      }
    }
  }
  finish() {
    this.canvas.getContext('2d').putImageData(this.img, 0, 0);
    this.glowCanvas.getContext('2d').putImageData(this.gimg, 0, 0);
    return { canvas: this.canvas, glow: this.hasGlow ? this.glowCanvas : null };
  }
}

// ---------- base body (skin tone, face, hair) ----------
function paintBase(p, tone, hair, { eyes = '#3b2a1e', mouth = '#7a3b2e', style = 'short' } = {}) {
  for (const part of ['head', 'body', 'armR', 'armL', 'legR', 'legL']) p.fill(part, tone, 0.04);
  p.clearHat();
  const dk = shade(tone, -0.14);
  // hair
  const H = p.faces('head');
  p.rect(...H.top, hair, 0.08);
  p.rect(H.back[0], H.back[1], 8, style === 'long' ? 8 : 6, hair, 0.08);
  for (const side of ['left', 'right']) {
    const [x, y] = H[side];
    p.rect(x, y, 8, style === 'long' ? 7 : 3, hair, 0.08);
  }
  // sideburns near the face
  p.f('head', 'right', 7, 3, hair);
  p.f('head', 'left', 0, 3, hair);
  // fringe
  p.rect(H.front[0], H.front[1], 8, 1, hair, 0.08);
  const fringe = style === 'spiky' ? [0, 1, 3, 4, 6, 7] : [0, 1, 2, 5, 6, 7];
  for (const x of fringe) p.f('head', 'front', x, 1, hair);
  p.f('head', 'front', 0, 2, hair);
  p.f('head', 'front', 7, 2, hair);
  if (style === 'long') for (let y = 2; y < 7; y++) (p.f('head', 'front', 0, y, hair), p.f('head', 'front', 7, y, hair));
  // eyebrows
  const brow = shade(hair, -0.1);
  p.f('head', 'front', 1, 3, brow);
  p.f('head', 'front', 2, 3, brow);
  p.f('head', 'front', 5, 3, brow);
  p.f('head', 'front', 6, 3, brow);
  // eyes
  p.f('head', 'front', 1, 4, '#ffffff');
  p.f('head', 'front', 2, 4, eyes);
  p.f('head', 'front', 5, 4, eyes);
  p.f('head', 'front', 6, 4, '#ffffff');
  // nose + mouth
  p.f('head', 'front', 3, 5, dk);
  p.f('head', 'front', 4, 5, dk);
  p.f('head', 'front', 2, 6, dk);
  p.f('head', 'front', 3, 6, mouth);
  p.f('head', 'front', 4, 6, mouth);
  p.f('head', 'front', 5, 6, dk);
  if (style === 'spiky') {
    // tufts on the hat layer
    for (const x of [0, 2, 4, 6]) p.f('hat', 'front', x, 0, hair);
    for (const x of [1, 3, 5, 7]) p.f('hat', 'top', x, 7, hair);
    for (const x of [0, 2, 4, 6]) p.f('hat', 'top', x, 6, hair);
  }
}

function shirt(p, col, { sleeve = 4, noise = 0.06 } = {}) {
  p.fill('body', col, noise);
  for (const a of ['armR', 'armL']) p.band(a, 0, sleeve, col, noise);
}
function pants(p, col, shoe = '#4a4a4a', shoeRows = 2) {
  for (const l of ['legR', 'legL']) {
    p.band(l, 0, 12, col, 0.06);
    p.band(l, 12 - shoeRows, 12, shoe, 0.05);
  }
}
function belt(p, col = '#5a3a20', buckle = '#ffd23f') {
  const F = p.faces('body');
  for (const k of ['front', 'back', 'left', 'right']) {
    const [x, y, w] = F[k];
    p.rect(x, y + 11, w, 1, col);
  }
  p.f('body', 'front', 3, 11, buckle);
  p.f('body', 'front', 4, 11, buckle);
}
function armorPart(p, part, base, light, dark, y0, y1) {
  p.band(part, y0, y1, base, 0.05);
  const F = p.faces(part);
  for (const k of ['front', 'back', 'left', 'right']) {
    const [x, y, w] = F[k];
    for (let i = 0; i < w; i++) p.px(x + i, y + y0, light);
    for (let i = 0; i < w; i++) p.px(x + i, y + y1 - 1, dark);
  }
}

// ---------- outfits ----------
export const OUTFITS = {
  classic(p) {
    shirt(p, '#2f7fe0');
    // emblem: shield with S
    const S = ['.###', '#...', '.##.', '...#', '###.'];
    for (let y = 0; y < 7; y++)
      for (let x = 0; x < 6; x++) {
        if (y === 6 && (x === 0 || x === 5)) continue; // pointed shield bottom
        p.f('body', 'front', 1 + x, 2 + y, y === 6 ? '#1b3f8f' : '#1d4fb0');
      }
    for (let y = 0; y < 5; y++) for (let x = 0; x < 4; x++) if (S[y][x] === '#') p.f('body', 'front', 2 + x, 3 + y, '#ffd23f');
    // sleeve cuffs
    for (const a of ['armR', 'armL']) p.band(a, 3, 4, '#2468c0', 0.02);
    belt(p);
    pants(p, '#2c3e75', '#3c3c44');
  },
  hoodie(p, hair) {
    shirt(p, '#d93a3a', { sleeve: 11 });
    for (const a of ['armR', 'armL']) p.band(a, 10, 11, '#b52a2a', 0.02);
    for (let y = 7; y < 11; y++) for (let x = 1; x < 7; x++) p.f('body', 'front', x, y, '#c03232');
    p.f('body', 'front', 3, 1, '#ffffff');
    p.f('body', 'front', 3, 2, '#ffffff');
    p.f('body', 'front', 4, 1, '#ffffff');
    p.f('body', 'front', 4, 2, '#ffffff');
    p.f('body', 'front', 4, 3, '#ffffff');
    // hood around head (hat layer back + sides lower rows)
    const F = p.faces('hat');
    p.rect(F.back[0], F.back[1] + 5, 8, 3, '#d93a3a', 0.05);
    p.rect(F.left[0], F.left[1] + 6, 8, 2, '#d93a3a', 0.05);
    p.rect(F.right[0], F.right[1] + 6, 8, 2, '#d93a3a', 0.05);
    pants(p, '#26262c', '#f2f2f2');
    for (const l of ['legR', 'legL']) p.band(l, 11, 12, '#d93a3a', 0);
  },
  ninja(p) {
    shirt(p, '#1e1e26', { sleeve: 12 });
    for (const a of ['armR', 'armL']) p.band(a, 10, 12, '#34343e', 0.02);
    const F = p.faces('body');
    for (const k of ['front', 'back', 'left', 'right']) {
      const [x, y, w] = F[k];
      p.rect(x, y + 7, w, 2, '#c42a2a', 0.04);
    }
    pants(p, '#1e1e26', '#111116', 3);
    // mask covers lower face + head wrap
    const H = p.faces('head');
    p.rect(...H.top, '#1e1e26', 0.05);
    p.rect(H.back[0], H.back[1], 8, 8, '#1e1e26', 0.05);
    p.rect(H.left[0], H.left[1], 8, 8, '#1e1e26', 0.05);
    p.rect(H.right[0], H.right[1], 8, 8, '#1e1e26', 0.05);
    for (let x = 0; x < 8; x++) {
      for (const y of [0, 1, 2, 5, 6, 7]) p.f('head', 'front', x, y, '#1e1e26');
      p.f('head', 'front', x, 2, '#c42a2a');
    }
    p.f('head', 'front', 0, 3, '#1e1e26');
    p.f('head', 'front', 7, 3, '#1e1e26');
    p.f('head', 'front', 0, 4, '#1e1e26');
    p.f('head', 'front', 7, 4, '#1e1e26');
    // headband on hat layer with tails
    const HT = p.faces('hat');
    for (const k of ['back', 'left', 'right']) p.rect(HT[k][0], HT[k][1] + 2, 8, 1, '#c42a2a');
    p.f('hat', 'back', 3, 3, '#c42a2a');
    p.f('hat', 'back', 4, 3, '#c42a2a');
    p.f('hat', 'back', 3, 4, '#a01f1f');
    p.f('hat', 'back', 4, 5, '#a01f1f');
  },
  knight(p) {
    armorSet(p, '#4fe0e0', '#b6fffb', '#2aa6b5', { visor: true });
  },
  gold(p) {
    armorSet(p, '#f5c542', '#fff0a0', '#c9951a', { visor: false });
    for (let x = 0; x < 8; x++) p.f('body', 'front', x, 5, '#c42a4a');
    p.f('body', 'front', 3, 5, '#ffd23f');
    p.f('body', 'front', 4, 5, '#ffd23f');
  },
  astro(p) {
    shirt(p, '#eef2f5', { sleeve: 12 });
    for (const a of ['armR', 'armL']) {
      p.band(a, 3, 4, '#ff7a2f', 0);
      p.band(a, 10, 12, '#9aa4b2', 0.02);
    }
    p.f('body', 'front', 2, 3, '#d33');
    p.f('body', 'front', 3, 3, '#3a7bd5');
    p.f('body', 'front', 4, 3, '#ffd23f');
    for (let y = 5; y < 9; y++) for (let x = 2; x < 6; x++) p.f('body', 'front', x, y, '#c9d1da');
    p.f('body', 'front', 3, 6, '#3a7bd5');
    p.f('body', 'front', 4, 7, '#ff7a2f');
    belt(p, '#9aa4b2', '#5a6270');
    pants(p, '#eef2f5', '#7a828e', 3);
    // helmet on hat layer: white shell + dark visor
    const HT = p.faces('hat');
    for (const k of Object.keys(HT)) p.rect(...HT[k], '#f4f6f8', 0.03);
    for (let y = 1; y < 7; y++)
      for (let x = 1; x < 7; x++) p.f('hat', 'front', x, y, y < 3 && x < 3 ? '#8fb7ff' : '#1b2a4a');
    p.f('hat', 'front', 2, 2, '#ffffff');
    p.rect(HT.bottom[0], HT.bottom[1], 8, 8, null);
  },
  lava(p) {
    for (const part of ['head', 'body', 'armR', 'armL', 'legR', 'legL']) p.fill(part, '#2a2220', 0.12);
    p.clearHat();
    const r = new RNG(42);
    const crack = (part, n) => {
      const F = p.faces(part);
      for (const k of ['front', 'back', 'left', 'right', 'top']) {
        const [fx, fy, w, h] = F[k];
        for (let i = 0; i < n; i++) {
          let x = r.int(0, w - 1), y = r.int(0, h - 1);
          for (let s = 0; s < 4; s++) {
            const c = r.chance(0.5) ? '#ff8a1f' : '#ffcf4a';
            p.px(fx + x, fy + y, c);
            p.glow(fx + x, fy + y, c);
            x = Math.max(0, Math.min(w - 1, x + r.int(-1, 1)));
            y = Math.max(0, Math.min(h - 1, y + 1));
          }
        }
      }
    };
    crack('body', 2);
    crack('armR', 1);
    crack('armL', 1);
    crack('legR', 1);
    crack('legL', 1);
    crack('head', 1);
    for (const x of [1, 2, 5, 6]) p.fg('head', 'front', x, 4, '#ffd040', '#ffb020');
    for (let x = 2; x < 6; x++) p.fg('head', 'front', x, 6, '#ff8a1f');
    // flame crown on hat layer
    for (let x = 0; x < 8; x++) if (x % 2 === 0) p.fg('hat', 'front', x, 0, '#ff8a1f');
  },
  ice(p, hair) {
    shirt(p, '#8fd3ff', { sleeve: 11 });
    for (const a of ['armR', 'armL']) p.band(a, 9, 11, '#ffffff', 0.02);
    for (let x = 0; x < 8; x++) p.f('body', 'front', x, 0, '#ffffff');
    for (let y = 0; y < 12; y++) p.f('body', 'front', 4, y, '#6ab8e8');
    // snowflake
    for (const [x, y] of [[2, 4], [2, 6], [1, 5], [3, 5], [2, 5]]) p.f('body', 'front', x, y, '#ffffff');
    pants(p, '#25406a', '#f2f6fa', 3);
    // beanie
    const HT = p.faces('hat');
    p.rect(...HT.top, '#3a7bd5', 0.05);
    for (const k of ['front', 'back', 'left', 'right']) {
      p.rect(HT[k][0], HT[k][1], 8, 2, '#3a7bd5', 0.05);
      p.rect(HT[k][0], HT[k][1] + 2, 8, 1, '#ffffff');
    }
    p.f('hat', 'top', 3, 3, '#ffffff');
    p.f('hat', 'top', 4, 4, '#ffffff');
  },
  robot(p) {
    for (const part of ['head', 'body', 'armR', 'armL', 'legR', 'legL']) p.fill(part, '#9aa4b2', 0.05);
    p.clearHat();
    const seams = (part) => {
      const F = p.faces(part);
      for (const k of ['front', 'back', 'left', 'right']) {
        const [x, y, w, h] = F[k];
        for (let i = 0; i < w; i++) p.px(x + i, y + Math.floor(h / 2), '#6a7482');
      }
    };
    ['body', 'armR', 'armL', 'legR', 'legL'].forEach(seams);
    for (let y = 3; y < 7; y++) for (let x = 2; x < 6; x++) p.fg('body', 'front', x, y, y === 3 || y === 6 || x === 2 || x === 5 ? '#2aa6b5' : '#7ffcff', '#40e8ff');
    for (let x = 1; x < 7; x++) p.fg('head', 'front', x, 4, '#40e8ff', '#40e8ff');
    for (let x = 0; x < 8; x++) p.f('head', 'front', x, 3, '#5a6270');
    for (let x = 2; x < 6; x++) p.f('head', 'front', x, 6, '#5a6270');
    for (const l of ['legR', 'legL']) p.band(l, 10, 12, '#5a6270', 0.03);
  },
  galaxy(p) {
    const r = new RNG(9);
    for (const part of ['body', 'armR', 'armL', 'legR', 'legL']) {
      const F = p.faces(part);
      for (const k of Object.keys(F)) {
        const [x, y, w, h] = F[k];
        for (let j = 0; j < h; j++)
          for (let i = 0; i < w; i++) {
            const t = (j / h + i / 20) % 1;
            const base = t < 0.5 ? '#2a1050' : '#1a1a6a';
            p.px(x + i, y + j, shade(base, (r.next() - 0.5) * 0.2));
            if (r.chance(0.06)) {
              p.px(x + i, y + j, '#ffffff');
              p.glow(x + i, y + j, '#ffffff');
            } else if (r.chance(0.05)) {
              p.px(x + i, y + j, '#ff5ac8');
              p.glow(x + i, y + j, '#a0307a');
            }
          }
      }
    }
    // hands stay skin: re-paint bottom of arms with skin? keep gloves dark
    const HT = p.faces('hat');
    for (const k of ['back', 'left', 'right']) p.rect(HT[k][0], HT[k][1], 8, 8, '#2a1050', 0.15);
    p.rect(...HT.top, '#2a1050', 0.15);
    for (let x = 0; x < 8; x++) p.f('hat', 'front', x, 0, '#2a1050');
    p.f('hat', 'top', 2, 2, '#ffffff');
    p.glow(...[HT.top[0] + 2, HT.top[1] + 2], '#ffffff');
  },
  rainbow(p) {
    const cols = ['#ff3b3b', '#ff9a2f', '#ffe03b', '#4bd34b', '#3b8bff', '#9b5cff'];
    for (const part of ['body', 'armR', 'armL', 'legR', 'legL']) {
      const F = p.faces(part);
      for (const k of Object.keys(F)) {
        const [x, y, w, h] = F[k];
        for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.px(x + i, y + j, cols[Math.floor((j / h) * 6) % 6]);
      }
    }
    for (const a of ['armR', 'armL']) p.band(a, 9, 12, '#ffffff', 0);
    // sunglasses
    for (let x = 1; x < 7; x++) p.f('head', 'front', x, 4, '#151515');
    p.f('head', 'front', 0, 3, '#151515');
    p.f('head', 'front', 7, 3, '#151515');
    p.f('head', 'front', 1, 3, '#151515');
    p.f('head', 'front', 2, 3, '#151515');
    p.f('head', 'front', 5, 3, '#151515');
    p.f('head', 'front', 6, 3, '#151515');
    p.f('head', 'front', 2, 3, '#7a7a7a');
    p.f('head', 'front', 5, 3, '#7a7a7a');
    for (const l of ['legR', 'legL']) p.band(l, 10, 12, '#ffffff', 0);
  },
  glitch(p) {
    const r = new RNG(13);
    for (const part of ['body', 'armR', 'armL', 'legR', 'legL']) {
      const F = p.faces(part);
      for (const k of Object.keys(F)) {
        const [x, y, w, h] = F[k];
        for (let j = 0; j < h; j++)
          for (let i = 0; i < w; i++) {
            let c = shade('#14101c', (r.next() - 0.5) * 0.3);
            p.px(x + i, y + j, c);
            if (r.chance(0.08)) {
              c = r.chance(0.5) ? '#ff2bd6' : '#22e8ff';
              p.px(x + i, y + j, c);
              p.glow(x + i, y + j, c);
            }
          }
      }
    }
    // visor
    for (let x = 0; x < 8; x++) p.fg('head', 'front', x, 4, x % 3 === 0 ? '#22e8ff' : '#ff2bd6');
    const HT = p.faces('hat');
    p.rect(...HT.top, '#14101c', 0.2);
    for (const k of ['back', 'left', 'right']) p.rect(HT[k][0], HT[k][1], 8, 3, '#14101c', 0.2);
  },
  gamer(p) {
    shirt(p, '#3cc85a');
    // controller icon
    for (let x = 1; x < 7; x++) p.f('body', 'front', x, 4, '#1e1e26');
    for (let x = 1; x < 7; x++) p.f('body', 'front', x, 5, '#1e1e26');
    p.f('body', 'front', 0, 5, '#1e1e26');
    p.f('body', 'front', 7, 5, '#1e1e26');
    p.f('body', 'front', 2, 4, '#ffffff');
    p.f('body', 'front', 5, 5, '#ff3b3b');
    p.f('body', 'front', 6, 4, '#3b8bff');
    pants(p, '#26262c', '#ffffff');
    for (const l of ['legR', 'legL']) p.band(l, 11, 12, '#ff3b3b', 0);
  },
  // ---- NPCs / mobs ----
  professor(p) {
    shirt(p, '#6b3fa0', { sleeve: 12 });
    for (const a of ['armR', 'armL']) p.band(a, 10, 12, '#f0d0b0', 0.02);
    for (const l of ['legR', 'legL']) p.band(l, 0, 12, '#6b3fa0', 0.06);
    for (const l of ['legR', 'legL']) p.band(l, 11, 12, '#3a2050', 0);
    for (let y = 0; y < 12; y++) p.f('body', 'front', 3, y, '#ffd23f');
    // beard + glasses
    for (let x = 1; x < 7; x++) for (const y of [6, 7]) p.f('head', 'front', x, y, '#f4f4f4');
    p.f('head', 'front', 2, 5, '#f4f4f4');
    p.f('head', 'front', 5, 5, '#f4f4f4');
    for (let x = 0; x < 8; x++) p.f('head', 'front', x, 3, '#3a2a1a');
    p.f('head', 'front', 1, 4, '#cfe8ff');
    p.f('head', 'front', 2, 4, '#1a3a5a');
    p.f('head', 'front', 5, 4, '#1a3a5a');
    p.f('head', 'front', 6, 4, '#cfe8ff');
    // wizard hat band (hat layer)
    const HT = p.faces('hat');
    p.rect(...HT.top, '#4a2a7a');
    for (const k of ['front', 'back', 'left', 'right']) p.rect(HT[k][0], HT[k][1], 8, 2, '#4a2a7a');
    p.f('hat', 'front', 3, 0, '#ffd23f');
  },
  builder(p) {
    shirt(p, '#8a8f99');
    for (let y = 0; y < 12; y++)
      for (const x of [0, 1, 6, 7]) {
        p.f('body', 'front', x, y, '#ff8a1f');
        p.f('body', 'back', x, y, '#ff8a1f');
      }
    for (let x = 0; x < 8; x++) p.f('body', 'back', x, 4, '#ffe07a');
    for (const x of [0, 1, 6, 7]) p.f('body', 'front', x, 4, '#ffe07a');
    belt(p, '#4a3020', '#c0c0c0');
    pants(p, '#3a5a9a', '#5a3a20');
    const HT = p.faces('hat');
    p.rect(...HT.top, '#ffcc1f');
    for (const k of ['front', 'back', 'left', 'right']) p.rect(HT[k][0], HT[k][1], 8, 2, '#ffcc1f');
    for (const k of ['front', 'back', 'left', 'right']) p.rect(HT[k][0], HT[k][1] + 2, 8, 1, '#e0a810');
  },
  villager(p) {
    shirt(p, '#7a5a3a', { sleeve: 12 });
    for (const a of ['armR', 'armL']) p.band(a, 10, 12, '#d8a878', 0.02);
    for (const l of ['legR', 'legL']) p.band(l, 0, 12, '#5a3a20', 0.06);
    belt(p, '#3a2414', '#c0a060');
  },
  zombie(p) {
    for (const part of ['head', 'armR', 'armL']) p.fill(part, '#b9c94a', 0.08);
    p.clearHat();
    shirt(p, '#1d69ac', { sleeve: 4 });
    const r = new RNG(5);
    for (let k = 0; k < 6; k++) p.f('body', 'front', r.int(0, 7), r.int(6, 11), '#b9c94a');
    for (const l of ['legR', 'legL']) p.band(l, 0, 12, '#93ad3c', 0.08);
    for (const l of ['legR', 'legL']) p.band(l, 10, 12, '#4a5a20', 0.04);
    // face
    p.fg('head', 'front', 1, 4, '#ff3030', '#ff2020');
    p.fg('head', 'front', 2, 4, '#ff3030', '#ff2020');
    p.fg('head', 'front', 5, 4, '#ff3030', '#ff2020');
    p.fg('head', 'front', 6, 4, '#ff3030', '#ff2020');
    for (let x = 2; x < 6; x++) p.f('head', 'front', x, 6, '#3a3a1a');
    p.f('head', 'front', 3, 7, '#ffffff');
    p.f('head', 'front', 5, 7, '#ffffff');
    // stitches
    p.f('head', 'front', 6, 1, '#5a6020');
    p.f('head', 'front', 6, 2, '#5a6020');
  },
};

function armorSet(p, base, light, dark, { visor }) {
  // chestplate & arms
  armorPart(p, 'body', base, light, dark, 0, 12);
  for (const a of ['armR', 'armL']) armorPart(p, a, base, light, dark, 0, 6);
  for (const a of ['armR', 'armL']) p.band(a, 9, 12, dark, 0.04);
  for (const l of ['legR', 'legL']) {
    armorPart(p, l, base, light, dark, 0, 8);
    p.band(l, 8, 12, shade(base, -0.2), 0.05);
    p.band(l, 11, 12, dark, 0);
  }
  // chest detail
  for (let y = 2; y < 9; y++) p.f('body', 'front', 0, y, light);
  p.f('body', 'front', 3, 3, light);
  p.f('body', 'front', 4, 3, light);
  // helmet on hat layer
  const HT = p.faces('hat');
  p.rect(...HT.top, base, 0.05);
  for (const k of ['back', 'left', 'right']) p.rect(HT[k][0], HT[k][1], 8, 6, base, 0.05);
  for (let x = 0; x < 8; x++) {
    p.f('hat', 'front', x, 0, light);
    p.f('hat', 'front', x, 1, base);
    p.f('hat', 'front', x, 2, x === 3 || x === 4 ? base : dark);
  }
  if (visor)
    for (let y = 3; y < 7; y++) {
      p.f('hat', 'front', 0, y, base);
      p.f('hat', 'front', 7, y, base);
      if (y >= 5) (p.f('hat', 'front', 3, y, base), p.f('hat', 'front', 4, y, base));
    }
  else for (let y = 3; y < 5; y++) (p.f('hat', 'front', 0, y, base), p.f('hat', 'front', 7, y, base));
}

export function paintSkin(outfit, toneIdx = 3, hairIdx = 0, style = 'short') {
  const p = new SkinPainter(17);
  const tone = SKIN_TONES[toneIdx] ?? SKIN_TONES[3];
  const hair = HAIR_COLORS[hairIdx] ?? HAIR_COLORS[0];
  paintBase(p, tone, hair, { style });
  (OUTFITS[outfit] || OUTFITS.classic)(p, hair);
  return p.finish();
}

export function paintNPC(kind) {
  const p = new SkinPainter(23);
  if (kind === 'professor') {
    paintBase(p, '#f0d0b0', '#e8e8e8');
    OUTFITS.professor(p);
  } else if (kind === 'builder') {
    paintBase(p, '#c98e5f', '#3b2416');
    OUTFITS.builder(p);
  } else if (kind === 'zombie') {
    paintBase(p, '#b9c94a', '#3a4a1a');
    OUTFITS.zombie(p);
  } else {
    paintBase(p, '#e5b48a', '#6b4423');
    OUTFITS.villager(p);
  }
  return p.finish();
}
